import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import '../../styles/page-titles.css';
import { SidebarContext } from "../../AppRoutes";
import PermissionGate from "../../services/PermissionGate";
import ConfirmDialog from "../../components/ConfirmDialog";
import styled, { keyframes } from 'styled-components';
import { AiOutlineSave } from 'react-icons/ai';
import { SlActionUndo } from 'react-icons/sl';
import { FiMinus, FiPlus, FiHash, FiLock, FiUnlock, FiRefreshCw, FiSettings, FiShield, FiDollarSign } from 'react-icons/fi';
import { getStoredUser } from '../../utils/safeStorage';
import { resetPedidoManual, getEmpresaWithObjId, getUltimoPedidoID, configurarResetPedido, updateEmpresaCancelamentoPedido, configurarPedidoMinimo } from "../../services/api";
import { toast } from "react-toastify";
import { formatDateTz } from '../../utils/timezone';
import { getCurrencySymbol, formatPriceWithSymbol } from '../../utils/currency';

// ============================================================================
// ANIMATIONS
// ============================================================================
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-10px); }
  to { opacity: 1; transform: translateX(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.02); }
`;

// ============================================================================
// STYLED COMPONENTS
// ============================================================================
const PageContainer = styled.div`
  display: flex;
  margin-left: ${({ $sidebar }) => ($sidebar ? '250px' : '100px')};
  min-height: 100vh;
  transition: margin-left 200ms ease;
  background: #ffffff;

  @media (max-width: 880px) {
    margin-left: 0;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  padding: 24px;
  animation: ${fadeIn} 0.4s ease-out;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

/* Header/TitleSection/TitleIcon/Title removed - using standardized page-titles.css classes */

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 480px) {
    width: 100%;
    flex-direction: column;
  }
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${({ $variant }) => $variant === 'primary' ? `
    background: linear-gradient(135deg, #318CD5 0%, #4FACFE 100%);
    color: white;
    box-shadow: 0 4px 14px rgba(49, 140, 213, 0.35);

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(49, 140, 213, 0.45);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }
  ` : `
    background: white;
    color: #64748b;
    border: 2px solid #e2e8f0;

    &:hover:not(:disabled) {
      border-color: #cbd5e1;
      color: #475569;
      background: #f8fafc;
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    font-size: 20px;
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const MainContent = styled.div`
  display: flex;
  gap: 24px;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const Sidebar = styled.div`
  width: 280px;
  flex-shrink: 0;

  @media (max-width: 900px) {
    width: 100%;
  }
`;

const TabsCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  border: 1px solid rgba(226, 232, 240, 0.8);
`;

const Tab = styled.button`
  width: 100%;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  border: none;
  background: ${({ $active }) => $active ? 'linear-gradient(135deg, rgba(49, 140, 213, 0.08) 0%, rgba(79, 172, 254, 0.08) 100%)' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: 4px solid ${({ $active }) => $active ? '#318CD5' : 'transparent'};
  position: relative;

  &:not(:last-child) {
    border-bottom: 1px solid #f1f5f9;
  }

  &:hover {
    background: ${({ $active }) => $active ? 'linear-gradient(135deg, rgba(49, 140, 213, 0.1) 0%, rgba(79, 172, 254, 0.1) 100%)' : '#f8fafc'};
  }
`;

const TabIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $active }) => $active ? 'linear-gradient(135deg, #318CD5 0%, #4FACFE 100%)' : '#f1f5f9'};
  transition: all 0.2s ease;

  svg {
    font-size: 18px;
    color: ${({ $active }) => $active ? 'white' : '#64748b'};
  }
`;

const TabLabel = styled.span`
  font-size: 14px;
  font-weight: ${({ $active }) => $active ? '600' : '500'};
  color: ${({ $active }) => $active ? '#1e293b' : '#64748b'};
  text-align: left;
`;

const ContentCard = styled.div`
  flex: 1;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(226, 232, 240, 0.8);
  overflow: hidden;
  animation: ${slideIn} 0.3s ease-out;
`;

const CardHeader = styled.div`
  padding: 24px 28px;
  border-bottom: 1px solid #f1f5f9;
  background: linear-gradient(135deg, rgba(49, 140, 213, 0.03) 0%, rgba(79, 172, 254, 0.03) 100%);
`;

const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #318CD5;
  }
`;

const CardBody = styled.div`
  padding: 28px;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 8px;

  span {
    color: #ef4444;
  }
`;

const InputWrapper = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 15px;
  color: #1e293b;
  background: ${({ disabled }) => disabled ? '#f8fafc' : 'white'};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #318CD5;
    box-shadow: 0 0 0 4px rgba(49, 140, 213, 0.1);
  }

  &:disabled {
    color: #64748b;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const NumberInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 200px;
`;

const NumberButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  border: 2px solid #e2e8f0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  svg {
    font-size: 18px;
    color: #64748b;
  }

  &:hover:not(:disabled) {
    border-color: #318CD5;
    background: rgba(49, 140, 213, 0.05);

    svg {
      color: #318CD5;
    }
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const NumberInput = styled.input`
  width: 80px;
  padding: 12px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  text-align: center;
  background: white;

  &:focus {
    outline: none;
    border-color: #318CD5;
    box-shadow: 0 0 0 4px rgba(49, 140, 213, 0.1);
  }

  /* Hide number spinners */
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const HintText = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 8px 0 0 0;
  line-height: 1.5;
`;

const ResetSection = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid #f1f5f9;
`;

const ResetDateField = styled.div`
  flex: 1;
  min-width: 200px;
`;

const ResetButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 20px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #318CD5 0%, #4FACFE 100%);
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  box-shadow: 0 4px 14px rgba(49, 140, 213, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(49, 140, 213, 0.4);
    animation: ${pulse} 0.3s ease;
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    font-size: 18px;
  }
`;

// Radio Options Styling
const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RadioOption = styled.label`
  display: flex;
  padding: 20px;
  border: 2px solid ${({ $checked }) => $checked ? '#318CD5' : '#e2e8f0'};
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ $checked }) => $checked ? 'linear-gradient(135deg, rgba(49, 140, 213, 0.05) 0%, rgba(79, 172, 254, 0.05) 100%)' : 'white'};

  &:hover {
    border-color: ${({ $checked }) => $checked ? '#318CD5' : '#cbd5e1'};
  }
`;

const RadioInput = styled.input`
  display: none;
`;

const RadioIndicator = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid ${({ $checked }) => $checked ? '#318CD5' : '#cbd5e1'};
  margin-right: 14px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &::after {
    content: '';
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${({ $checked }) => $checked ? '#318CD5' : 'transparent'};
    transition: all 0.2s ease;
  }
`;

const RadioContent = styled.div`
  flex: 1;
`;

const RadioTitle = styled.span`
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
`;

const RadioDescription = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
`;

const PasswordSection = styled.div`
  margin-top: 24px;
  padding: 24px;
  background: #f8fafc;
  border-radius: 14px;
  animation: ${fadeIn} 0.3s ease-out;
`;

const PasswordFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 400px;
`;

const ChangePasswordLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding: 0;
  border: none;
  background: none;
  color: #318CD5;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #2563eb;
    text-decoration: underline;
  }
`;

// ============================================================================
// COMPONENT
// ============================================================================
const MeusPedidosCfg = () => {
    const { t } = useTranslation('orders');
    const userParse = getStoredUser();
    const empresa = localStorage.getItem('empresa');
    const empresaParse = JSON.parse(empresa);
    const idEmpresa = empresaParse.id_empresa;
    const empresaObjId = empresaParse._id;

    const [atualSeqId, setAtualSeqId] = useState(0);
    const [daysToReset, setDaysToReset] = useState(0);
    const [lastResetedDate, setLastResetedDate] = useState(null);
    const [refresh, setRefresh] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [tela, setTela] = useState("tela1");
    const [cancelOption, setCancelOption] = useState("noPassword");
    const [cancelPassword, setCancelPassword] = useState("");
    const [showInputNewPassword, setShowInputNewPassword] = useState(false);
    const [newCancelPassword, setNewCancelPassword] = useState("");
    const [currentCancelPassword, setCurrentCancelPassword] = useState("");
    const [hasCancelPassword, setHasCancelPassword] = useState(false);
    const [definindoNovaSenha, setDefinindoNovaSenha] = useState(true);
    const [pedidoMinimo, setPedidoMinimo] = useState(0);
    const [pedidoMinimoDisplay, setPedidoMinimoDisplay] = useState('');
    const { sidebar } = useContext(SidebarContext);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const empresaTemp = await getEmpresaWithObjId(empresaObjId);
                if (empresaTemp.data.empresa?.dias_para_reset) {
                    setDaysToReset(empresaTemp.data.empresa.dias_para_reset);
                }
                if (empresaTemp.data.empresa?.last_reset_date) {
                    const dataFormatada = formatDateTz(empresaTemp.data.empresa.last_reset_date);
                    setLastResetedDate(dataFormatada);
                }
                if (empresaTemp.data.empresa?.has_cancel_password) {
                    setCancelOption("standardPassword");
                    setDefinindoNovaSenha(false);
                }
                setHasCancelPassword(empresaTemp.data.empresa.has_cancel_password);
                if (empresaTemp.data.empresa?.pedido_minimo !== undefined) {
                    const val = empresaTemp.data.empresa.pedido_minimo || 0;
                    setPedidoMinimo(val);
                    setPedidoMinimoDisplay(val > 0 ? val.toFixed(2) : '');
                }
                const ultimoPedidoId = await getUltimoPedidoID();
                if (ultimoPedidoId.data?.ultimoPedidoId) {
                    setAtualSeqId(ultimoPedidoId.data.ultimoPedidoId);
                }
            } catch (error) {
                console.error("Erro ao buscar dados da empresa:", error);
            }
        };
        fetchData();
    }, [refresh, empresaObjId]);

    const handleBack = () => navigate("/");

    const handleChangePassword = () => {
        setShowInputNewPassword(!showInputNewPassword);
        setDefinindoNovaSenha(false);
    };

    const handleOpenDialog = () => setConfirmOpen(true);

    const handleReset = () => {
        resetPedidoManual(empresaObjId).then(cast => {
            if (cast.status === 200) {
                toast(cast.data.msg, { autoClose: 5000, type: "success" });
            } else {
                toast(cast.data.msg, { autoClose: 5000, type: "error" });
            }
        }).catch(err => {
            toast(err.response?.data?.msg || t('settings.toasts.unexpectedError'), { autoClose: 5000, type: "error" });
        }).finally(() => {
            setRefresh(!refresh);
        });
    };

    const saveChanges = async () => {
        setIsSubmitting(true);
        configurarResetPedido(empresaObjId, daysToReset).then(cast => {
            if (cast.status === 200) {
                toast(cast.data.msg, { autoClose: 5000, type: "success" });
            } else {
                toast(cast.data.msg, { autoClose: 5000, type: "error" });
            }
        }).catch(err => {
            toast(err.response?.data?.msg || t('settings.toasts.unexpectedError'), { autoClose: 5000, type: "error" });
        }).finally(() => {
            setIsSubmitting(false);
        });
    };

    const alterarCancelamentoPedido = async () => {
        setIsSubmitting(true);

        if (cancelOption === "noPassword") {
            const payload = {
                id_empresa: empresaObjId,
                has_cancel_password: false,
            };

            try {
                const response = await updateEmpresaCancelamentoPedido(payload);
                if (response.status === 200) {
                    toast(response.data.msg, { autoClose: 5000, type: "success" });
                    setHasCancelPassword(false);
                    setShowInputNewPassword(false);
                    setRefresh(!refresh);
                    setDefinindoNovaSenha(true);
                } else {
                    toast(response.data.msg, { autoClose: 5000, type: "error" });
                }
            } catch (err) {
                toast(err.response?.data?.msg || t('settings.toasts.unexpectedError'), { autoClose: 5000, type: "error" });
            } finally {
                setIsSubmitting(false);
            }
            return;
        }

        let senha, senhaAtual, novaSenha;
        if (definindoNovaSenha) {
            senha = cancelPassword;
            senhaAtual = null;
            novaSenha = null;
        } else {
            senha = null;
            senhaAtual = currentCancelPassword;
            novaSenha = newCancelPassword;
        }

        const payload = {
            id_empresa: empresaObjId,
            has_cancel_password: true,
            senha,
            senhaAtual,
            novaSenha
        };

        if (!showInputNewPassword && !definindoNovaSenha) {
            setIsSubmitting(false);
            return;
        }

        try {
            const response = await updateEmpresaCancelamentoPedido(payload);
            if (response.status === 200) {
                toast(response.data.msg, { autoClose: 5000, type: "success" });
                setHasCancelPassword(true);
                setShowInputNewPassword(false);
                setCancelPassword("");
                setNewCancelPassword("");
                setCurrentCancelPassword("");
            } else {
                toast(response.data.msg, { autoClose: 5000, type: "error" });
            }
        } catch (err) {
            toast(err.response?.data?.msg || t('settings.toasts.unexpectedError'), { autoClose: 5000, type: "error" });
        } finally {
            setIsSubmitting(false);
        }
    };

    const salvarPedidoMinimo = async () => {
        setIsSubmitting(true);
        configurarPedidoMinimo(pedidoMinimo).then(cast => {
            if (cast.status === 200) {
                toast(cast.data.msg, { autoClose: 5000, type: "success" });
            } else {
                toast(cast.data.msg, { autoClose: 5000, type: "error" });
            }
        }).catch(err => {
            toast(err.response?.data?.msg || t('settings.toasts.unexpectedError'), { autoClose: 5000, type: "error" });
        }).finally(() => {
            setIsSubmitting(false);
        });
    };

    const handleDecrement = (value) => {
        if (value > 0) setDaysToReset(value - 1);
    };

    const handleIncrement = (value) => {
        setDaysToReset(value + 1);
    };

    return (
        <PermissionGate permissions={['default']}>
            <ConfirmDialog
                title={t('settings.dialogs.resetTitle')}
                open={confirmOpen}
                setOpen={setConfirmOpen}
                onConfirm={handleReset}
            >
                <div>
                    {t('settings.dialogs.resetMessage')}
                    <br />
                    <span style={{ fontSize: 12, color: '#64748b' }}>
                        {t('settings.dialogs.resetHint')}
                    </span>
                </div>
            </ConfirmDialog>

            <PageContainer $sidebar={sidebar}>
                <ContentWrapper>
                    <div className="pedeja-page-header">
                        <div className="page-title-with-subtitle">
                            <div className="page-title-with-icon">
                                <div className="page-icon"><FiSettings /></div>
                                <h1 className="page-title">{t('settings.pageTitle')}</h1>
                            </div>
                            <p className="page-subtitle">{t('settings.pageSubtitle', 'Configure as opcoes dos seus pedidos')}</p>
                        </div>

                        <ButtonGroup>
                            <Button
                                $variant="primary"
                                onClick={tela === "tela1" ? saveChanges : tela === "tela2" ? alterarCancelamentoPedido : salvarPedidoMinimo}
                                disabled={isSubmitting}
                            >
                                <AiOutlineSave />
                                {isSubmitting ? t('settings.buttons.saving') : t('settings.buttons.save')}
                            </Button>
                            <Button onClick={handleBack}>
                                <SlActionUndo />
                                {t('settings.buttons.back')}
                            </Button>
                        </ButtonGroup>
                    </div>

                    <MainContent>
                        <Sidebar>
                            <TabsCard>
                                <Tab
                                    $active={tela === "tela1"}
                                    onClick={() => setTela("tela1")}
                                >
                                    <TabIcon $active={tela === "tela1"}>
                                        <FiHash />
                                    </TabIcon>
                                    <TabLabel $active={tela === "tela1"}>
                                        {t('settings.tabs.sequence')}
                                    </TabLabel>
                                </Tab>
                                <Tab
                                    $active={tela === "tela2"}
                                    onClick={() => setTela("tela2")}
                                >
                                    <TabIcon $active={tela === "tela2"}>
                                        <FiShield />
                                    </TabIcon>
                                    <TabLabel $active={tela === "tela2"}>
                                        {t('settings.tabs.cancel')}
                                    </TabLabel>
                                </Tab>
                                <Tab
                                    $active={tela === "tela3"}
                                    onClick={() => setTela("tela3")}
                                >
                                    <TabIcon $active={tela === "tela3"}>
                                        <FiDollarSign />
                                    </TabIcon>
                                    <TabLabel $active={tela === "tela3"}>
                                        {t('settings.tabs.minimumOrder')}
                                    </TabLabel>
                                </Tab>
                            </TabsCard>
                        </Sidebar>

                        <ContentCard key={tela}>
                            {tela === "tela1" && (
                                <>
                                    <CardHeader>
                                        <CardTitle>
                                            <FiHash />
                                            {t('settings.sequence.title')}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardBody>
                                        <FormGroup>
                                            <Label>{t('settings.sequence.lastOrderNumber')}</Label>
                                            <InputWrapper>
                                                <Input
                                                    type="number"
                                                    value={atualSeqId}
                                                    disabled
                                                    readOnly
                                                />
                                            </InputWrapper>
                                        </FormGroup>

                                        <FormGroup>
                                            <Label>
                                                {t('settings.sequence.daysToReset')} <span>{t('settings.sequence.required')}</span>
                                            </Label>
                                            <NumberInputWrapper>
                                                <NumberButton
                                                    type="button"
                                                    onClick={() => handleDecrement(daysToReset)}
                                                    disabled={daysToReset === 0}
                                                >
                                                    <FiMinus />
                                                </NumberButton>
                                                <NumberInput
                                                    type="number"
                                                    value={daysToReset}
                                                    onChange={(e) => setDaysToReset(parseInt(e.target.value) || 0)}
                                                    min={0}
                                                />
                                                <NumberButton
                                                    type="button"
                                                    onClick={() => handleIncrement(daysToReset)}
                                                >
                                                    <FiPlus />
                                                </NumberButton>
                                            </NumberInputWrapper>
                                            <HintText>
                                                {daysToReset <= 0
                                                    ? t('settings.sequence.resetInfoNever')
                                                    : t('settings.sequence.resetInfoDays', { days: daysToReset })
                                                }
                                            </HintText>
                                        </FormGroup>

                                        <ResetSection>
                                            <ResetDateField>
                                                <Label>{t('settings.sequence.lastResetDate')}</Label>
                                                <Input
                                                    type="text"
                                                    value={lastResetedDate || t('settings.sequence.never')}
                                                    disabled
                                                    readOnly
                                                />
                                            </ResetDateField>
                                            <ResetButton type="button" onClick={handleOpenDialog}>
                                                <FiRefreshCw />
                                                {t('settings.buttons.resetNow')}
                                            </ResetButton>
                                        </ResetSection>
                                    </CardBody>
                                </>
                            )}

                            {tela === "tela2" && (
                                <>
                                    <CardHeader>
                                        <CardTitle>
                                            <FiShield />
                                            {t('settings.cancel.title')}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardBody>
                                        <RadioGroup>
                                            <RadioOption $checked={cancelOption === "noPassword"}>
                                                <RadioInput
                                                    type="radio"
                                                    name="cancelOption"
                                                    value="noPassword"
                                                    checked={cancelOption === "noPassword"}
                                                    onChange={() => setCancelOption("noPassword")}
                                                />
                                                <RadioIndicator $checked={cancelOption === "noPassword"} />
                                                <RadioContent>
                                                    <RadioTitle>
                                                        <FiUnlock style={{ marginRight: 8, verticalAlign: 'middle' }} />
                                                        {t('settings.cancel.noPasswordOption')}
                                                    </RadioTitle>
                                                    <RadioDescription>
                                                        {t('settings.cancel.noPasswordDescription')}
                                                    </RadioDescription>
                                                </RadioContent>
                                            </RadioOption>

                                            <RadioOption $checked={cancelOption === "standardPassword"}>
                                                <RadioInput
                                                    type="radio"
                                                    name="cancelOption"
                                                    value="standardPassword"
                                                    checked={cancelOption === "standardPassword"}
                                                    onChange={() => setCancelOption("standardPassword")}
                                                />
                                                <RadioIndicator $checked={cancelOption === "standardPassword"} />
                                                <RadioContent>
                                                    <RadioTitle>
                                                        <FiLock style={{ marginRight: 8, verticalAlign: 'middle' }} />
                                                        {t('settings.cancel.standardPasswordOption')}
                                                    </RadioTitle>
                                                    <RadioDescription>
                                                        {t('settings.cancel.standardPasswordDescription')}
                                                    </RadioDescription>
                                                </RadioContent>
                                            </RadioOption>
                                        </RadioGroup>

                                        {cancelOption === "standardPassword" && (
                                            <PasswordSection>
                                                <PasswordFields>
                                                    {!hasCancelPassword || showInputNewPassword ? (
                                                        <>
                                                            {definindoNovaSenha ? (
                                                                <FormGroup style={{ marginBottom: 0 }}>
                                                                    <Label>
                                                                        {t('settings.cancel.setPassword')} <span>{t('settings.sequence.required')}</span>
                                                                    </Label>
                                                                    <Input
                                                                        type="password"
                                                                        value={cancelPassword}
                                                                        onChange={(e) => setCancelPassword(e.target.value)}
                                                                        placeholder="********"
                                                                    />
                                                                </FormGroup>
                                                            ) : (
                                                                <>
                                                                    <FormGroup>
                                                                        <Label>
                                                                            {t('settings.cancel.currentPassword')} <span>{t('settings.sequence.required')}</span>
                                                                        </Label>
                                                                        <Input
                                                                            type="password"
                                                                            value={currentCancelPassword}
                                                                            onChange={(e) => setCurrentCancelPassword(e.target.value)}
                                                                            placeholder="********"
                                                                        />
                                                                    </FormGroup>
                                                                    <FormGroup style={{ marginBottom: 0 }}>
                                                                        <Label>
                                                                            {t('settings.cancel.newPassword')} <span>{t('settings.sequence.required')}</span>
                                                                        </Label>
                                                                        <Input
                                                                            type="password"
                                                                            value={newCancelPassword}
                                                                            onChange={(e) => setNewCancelPassword(e.target.value)}
                                                                            placeholder="********"
                                                                        />
                                                                    </FormGroup>
                                                                </>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <FormGroup style={{ marginBottom: 0 }}>
                                                            <Label>
                                                                {t('settings.cancel.password')} <span>{t('settings.sequence.required')}</span>
                                                            </Label>
                                                            <Input
                                                                type="password"
                                                                value="******"
                                                                disabled
                                                            />
                                                        </FormGroup>
                                                    )}

                                                    {hasCancelPassword && (
                                                        <ChangePasswordLink
                                                            type="button"
                                                            onClick={handleChangePassword}
                                                        >
                                                            {!showInputNewPassword
                                                                ? t('settings.buttons.changePassword')
                                                                : t('settings.buttons.cancelChangePassword')
                                                            }
                                                        </ChangePasswordLink>
                                                    )}
                                                </PasswordFields>
                                            </PasswordSection>
                                        )}
                                    </CardBody>
                                </>
                            )}

                            {tela === "tela3" && (
                                <>
                                    <CardHeader>
                                        <CardTitle>
                                            <FiDollarSign />
                                            {t('settings.minimumOrder.title')}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardBody>
                                        <FormGroup>
                                            <Label>{t('settings.minimumOrder.label')}</Label>
                                            <InputWrapper>
                                                <Input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={pedidoMinimoDisplay}
                                                    onChange={(e) => {
                                                        // Permitir apenas números, ponto e vírgula
                                                        const raw = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
                                                        setPedidoMinimoDisplay(e.target.value.replace(/[^0-9.,]/g, ''));
                                                        const num = parseFloat(raw);
                                                        setPedidoMinimo(isNaN(num) ? 0 : num);
                                                    }}
                                                    onBlur={() => {
                                                        // Formatar ao sair do campo
                                                        if (pedidoMinimo > 0) {
                                                            setPedidoMinimoDisplay(pedidoMinimo.toFixed(2));
                                                        } else {
                                                            setPedidoMinimoDisplay('');
                                                        }
                                                    }}
                                                    placeholder="0,00"
                                                    style={{ paddingLeft: '48px' }}
                                                />
                                                <span style={{
                                                    position: 'absolute',
                                                    left: '14px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    color: '#94a3b8',
                                                    fontWeight: 600,
                                                    fontSize: '14px',
                                                    pointerEvents: 'none'
                                                }}>
                                                    {getCurrencySymbol()}
                                                </span>
                                            </InputWrapper>
                                            <HintText>
                                                {pedidoMinimo > 0
                                                    ? t('settings.minimumOrder.hintWithValue', { value: formatPriceWithSymbol(pedidoMinimo) })
                                                    : t('settings.minimumOrder.hint')
                                                }
                                            </HintText>
                                        </FormGroup>
                                    </CardBody>
                                </>
                            )}
                        </ContentCard>
                    </MainContent>
                </ContentWrapper>
            </PageContainer>
        </PermissionGate>
    );
};

export default MeusPedidosCfg;
